//
//  Users.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 21, 2026
//  Author: Bob Keathley
//
//  Tenant console Users page: lists managed users, invites new accounts with
//  roles limited to what the signed-in role may grant, and shows sign-in mail
//  status and device sessions. Self and admin device routes differ (spec 6m).
//
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { DEVICE_REVOKE_MIN_ROLE, MAIL_POLL_MS, MAIL_POLL_WINDOW_MS, isBadMail, mailTitle, pickMail, type MailItem, type MailStatus, type ManagedUser } from '@shared/api/types'
import SharedDevicesPanel from '@shared/components/DevicesPanel'
import { atLeast, grantableTenantRoles, type Session, type TenantRole } from '@shared/auth'

const when = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleDateString() : '—')

// §6l sign-in email chip: delivered green, sent/queued neutral, bounced/undelivered red, unknown grey.
function SignInMail({ path, token, refreshKey }: { path: string; token: string; refreshKey: number }) {
  const [item, setItem] = useState<MailItem | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    const once = async () => {
      const r = await api<{ items: MailItem[] }>(path, {}, token)
      if (alive) setItem(isOk(r) ? pickMail(r.data.items) : { workflow: 'sign-in email', sent_at: 0, status: 'unknown', status_at: null })
    }
    once()
    const t0 = Date.now()
    const h = setInterval(() => { if (Date.now() - t0 >= MAIL_POLL_WINDOW_MS) clearInterval(h); else once() }, MAIL_POLL_MS)
    return () => { alive = false; clearInterval(h) }
  }, [path, token, refreshKey])
  if (item === undefined) return <span className="hint">sign-in email: …</span>
  const status: MailStatus = item?.status ?? 'unknown'
  const cls = status === 'delivered' ? 'ok' : isBadMail(status) ? 'off' : ''
  return (
    <span>
      <span className={`pill ${cls}`} style={status === 'unknown' ? { color: 'var(--dim)' } : undefined} title={mailTitle(item)}>sign-in email: {status}</span>
      {isBadMail(status) && <span className="hint"> Check the address — the last email did not arrive.</span>}
    </span>
  )
}

/** Tenant wrapper over the shared panel: self vs admin routes (§6m). */
export function DevicesPanel({ token, userId, self, canRevoke, onlyMachine }: { token: string; userId: string; self: boolean; canRevoke: boolean; onlyMachine?: string }) {
  return (
    <SharedDevicesPanel token={token} canRevoke={canRevoke} onlyMachine={onlyMachine}
      listPath={self ? '/users/me/devices' : `/users/${userId}/devices`}
      revokePath={(id) => (self ? `/auth/device/${id}` : `/users/${userId}/devices/${id}`)} />
  )
}

export default function Users({ session }: { session: Session }) {
  const me = session.user
  const grantable = grantableTenantRoles(me.role)
  const [items, setItems] = useState<ManagedUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', role: (grantable[0] ?? 'member') as TenantRole, display_name: '' })
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [mailTick, setMailTick] = useState<Record<string, number>>({})
  const bump = (id: string) => setMailTick((m) => ({ ...m, [id]: (m[id] ?? 0) + 1 }))
  const opts = useCallback(() => ({}), [])
  const [openDevices, setOpenDevices] = useState<string | null>(null)

  const load = useCallback(async () => {
    const r = await api<{ items: ManagedUser[] }>('/users', opts(), session.token)
    if (isOk(r)) { setItems(r.data.items); setError(null) } else { setItems([]); setError(r.error.message) }
  }, [opts, session.token])
  useEffect(() => { load() }, [load])

  async function create(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null); setNotice(null)
    const r = await api<ManagedUser>('/users', { method: 'POST', body: JSON.stringify(form), ...opts() }, session.token)
    setBusy(false)
    if (!isOk(r)) { setError(r.error.code === 'mail_unavailable' ? "Couldn't send the email; try Resend." : r.error.message); load(); return }
    setNotice(`Account created: sign-in details to ${r.data.email} queued — delivery status will update.`)
    setForm({ email: '', role: grantable[0] ?? 'member', display_name: '' })
    load()
  }
  // `409 last_owner`: the tenant must keep at least one active owner.
  const fail = (e: { code: string; message: string }) => setError(e.code === 'last_owner' ? "Can't remove the last owner." : e.message)
  async function patch(u: ManagedUser, body: Partial<Pick<ManagedUser, 'role' | 'is_active'>>) {
    setNotice(null)
    const r = await api<ManagedUser>(`/users/${u.id}`, { method: 'PATCH', body: JSON.stringify(body), ...opts() }, session.token)
    if (!isOk(r)) fail(r.error); else { setError(null); load() }
  }
  // Re-send sign-in details (a new temp password) for a user who hasn't changed it yet; reset for one who did (their current password keeps working until the link is used).
  async function sendEmail(u: ManagedUser, path: 'credentials' | 'password-reset') {
    setNotice(null); setError(null)
    const r = await api<unknown>(`/users/${u.id}/${path}`, { method: 'POST', ...opts() }, session.token)
    if (!isOk(r)) { setError(r.error.code === 'already_set' ? 'This user has already chosen their own password; send a password reset instead.' : r.error.code === 'mail_unavailable' ? "Couldn't send the email; try Resend." : r.error.message); load(); return }
    setNotice(path === 'credentials' ? `Sign-in details to ${u.email} queued — delivery status will update.` : `Password reset to ${u.email} queued — delivery status will update.`)
    bump(u.id)
    load()
  }
  async function deactivate(u: ManagedUser) {
    if (!confirm(`Deactivate ${u.email}? They will be signed out and unable to log in.`)) return
    const r = await api<ManagedUser>(`/users/${u.id}`, { method: 'DELETE', ...opts() }, session.token)
    if (!isOk(r)) fail(r.error); else { setError(null); load() }
  }
  // §3: a role may only grant a role at or below its own; nobody may deactivate themselves.
  const canTouch = (u: ManagedUser) => u.id !== me.id && atLeast(me.role, u.role as TenantRole)

  return (
    <>
      <h2>Users</h2>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}
      <table>
        <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Created</th><th>Last login</th><th></th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={7} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && <tr><td colSpan={7} className="hint">No users.</td></tr>}
          {items?.map((u) => (
            <tr key={u.id}>
              <td className="mono">{u.email}{u.id === me.id && <span className="hint"> (you)</span>}</td>
              <td>{u.display_name}</td>
              <td>
                {canTouch(u) ? (
                  <select value={u.role} onChange={(e) => patch(u, { role: e.target.value as TenantRole })} style={{ width: 'auto' }}>
                    {grantable.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : <span className={`pill ${u.role}`}>{u.role}</span>}
              </td>
              <td>{u.is_active ? <span className="pill">active</span> : <span className="pill off">inactive</span>}</td>
              <td className="hint">{when(u.created_at)}</td>
              <td className="hint">{when(u.last_login_at)}</td>
              <td style={{ textAlign: 'right' }}>
                {u.is_active && !u.password_set && (
                  <SignInMail path={`/users/${u.id}/mail`} token={session.token} refreshKey={mailTick[u.id] ?? 0} />
                )}{' '}
                {u.is_active && canTouch(u) && (u.password_set
                  ? <button className="btn ghost sm" onClick={() => sendEmail(u, 'password-reset')}>Send password reset</button>
                  : <button className="btn ghost sm" onClick={() => sendEmail(u, 'credentials')}>Resend sign-in details</button>)}{' '}
                <button className="btn ghost sm" aria-expanded={openDevices === u.id} onClick={() => setOpenDevices(openDevices === u.id ? null : u.id)}>Devices</button>{' '}
                {canTouch(u) && (u.is_active
                  ? <button className="btn danger sm" onClick={() => deactivate(u)}>Deactivate</button>
                  : <button className="btn ghost sm" onClick={() => patch(u, { is_active: true })}>Reactivate</button>)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {openDevices && items?.find((u) => u.id === openDevices) && (
        <>
          <h2>Devices — {items.find((u) => u.id === openDevices)!.email}</h2>
          <DevicesPanel token={session.token} userId={openDevices} self={openDevices === me.id} canRevoke={openDevices === me.id || atLeast(me.role, DEVICE_REVOKE_MIN_ROLE)} />
        </>
      )}
      <h2>Add user</h2>
      <p className="hint">The new user is emailed their sign-in details (a temporary password in a separate email) and must choose their own password at first sign-in.</p>
      <form onSubmit={create} className="row" aria-label="Add user">
        <div><label htmlFor="n-email">Email</label><input id="n-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><label htmlFor="n-name">Display name</label><input id="n-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></div>
        <div className="fixed"><label htmlFor="n-role">Role</label><select id="n-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as TenantRole })} style={{ width: 'auto' }}>{grantable.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
        <div className="fixed"><button className="btn" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add'}</button></div>
      </form>
      <p className="hint">Users are deactivated, never deleted: the ledger references actors by id.</p>
    </>
  )
}
